import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, MapPin, Plus, Search, Sparkles, X } from "lucide-react";
import { api, formatMoney } from "../../api";
import { useAuth } from "../../auth";
import { useApi } from "../../hooks";
import {
  adPreview, DAYS, formatCustomerId, hourLabel, isBrandCampaign, lines, samePhone,
  type CallHours, type CheckResult, type Defaults, type DraftAd, type DraftAssets, type FieldNote, type Place, type ProposeResult, type Sitelink, type SpecLocation,
} from "../../campaigns";
import { ErrorState, PageHeader, PageSkeleton } from "../Ui";
import { phoneLabel } from "../../lsa-call-format";

type AdText = { headlines: string; descriptions: string };
type Group = { id: number; name: string; keywords: string; final_url: string; path1: string; path2: string; ads: AdText[] };
type GroupNote = { error?: string; problems?: string[][] };

let nextGroupId = 1;
const blankGroup = (): Group => ({ id: nextGroupId++, name: "", keywords: "", final_url: "", path1: "", path2: "", ads: [{ headlines: "", descriptions: "" }, { headlines: "", descriptions: "" }] });
const blankLink = (): Sitelink => ({ text: "", description1: "", description2: "", final_url: "" });
const filled = (l: Sitelink) => !!(l.text.trim() || l.description1.trim() || l.description2.trim() || l.final_url.trim());
const AD_STYLES = ["Emotional", "Direct"];
// Fields the form shows next to their inputs; anything else (Google's own refusals) is listed in the footer.
const SHOWN = /^(name|daily_budget|bidding|max_cpc|locations|match_type|final_url|negatives|ad_groups|sitelinks|callouts|snippet|phone|call_hours)$|^ad_groups\.\d+\.(name|keywords|final_url|path|ads)(\.\d+)?$/;
const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/** "https://www.junk-king.com/locations/sacramento/?x=1" → "junk-king.com/locations/sacramento". */
const shortUrl = (u: string) => u.replace(/^https?:\/\/(www\.)?/i, "").replace(/[?#].*$/, "").replace(/\/$/, "");

/**
 * A new Google Ads Search campaign built to No Limits' SOPs (KB-004, KB-018 §4, KB-029 §8-9):
 * budget and bidding, places, ad groups with two ads each, blocked searches, sitelinks,
 * callouts and a Services snippet, the call button and its hours, then Check with Google
 * and Create. It is created paused.
 */
export default function CampaignBuilder({ clientId, account: initialAccount, onDone }: { clientId: number; account: string; onDone: (message: string) => void }) {
  const { data, loading, error, reload } = useApi<Defaults>(`/api/ads-manager/${clientId}/defaults`);
  if (loading && !data) return <><PageHeader title="New campaign" /><PageSkeleton cards={2} /></>;
  if (error || !data) return <><PageHeader title="New campaign" /><ErrorState message={error || "The form could not load."} onRetry={reload} /></>;
  return <Form clientId={clientId} defaults={data} initialAccount={initialAccount} onDone={onDone} />;
}

function Form({ clientId, defaults, initialAccount, onDone }: { clientId: number; defaults: Defaults; initialAccount: string; onDone: (message: string) => void }) {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const navigate = useNavigate();
  const L = defaults.limits;
  const back = `/ads-campaigns?client_id=${clientId}${initialAccount ? `&account=${initialAccount}` : ""}`;
  const [account, setAccount] = useState(initialAccount || defaults.accounts[0]?.customer_id || "");
  const [name, setName] = useState(`${defaults.client.name} | Search`);
  const [budget, setBudget] = useState("");
  const [bidding, setBidding] = useState<"max_conversions" | "manual_cpc">("max_conversions");
  const [maxCpc, setMaxCpc] = useState("");
  const [places, setPlaces] = useState<SpecLocation[]>([]);
  const [match, setMatch] = useState<"phrase" | "exact">("phrase");
  const [finalUrl, setFinalUrl] = useState(defaults.final_url);
  const [groups, setGroups] = useState<Group[]>(() => [blankGroup()]);
  const [negatives, setNegatives] = useState(defaults.standard_negatives.join("\n"));
  const [sitelinks, setSitelinks] = useState<Sitelink[]>(() => Array.from({ length: L.sitelinks[0] }, blankLink));
  const [callouts, setCallouts] = useState("");
  const [services, setServices] = useState("");
  // US numbers read "(916) 299-7920" here as on Tracking; what is sent stays the number as stored (see spec).
  const [phone, setPhone] = useState(phoneLabel(defaults.phone) ?? "");
  // What this Google Ads account already uses: its ads' landing pages and its call buttons' numbers.
  const hints = useApi<{ landing_pages: { url: string; ads: number }[]; phones: { number: string; campaigns: string[] }[] }>(account ? `/api/ads-manager/${clientId}/account-hints?account=${account}` : null);
  const hintUrls = (hints.data?.landing_pages || []).filter((h) => h.url !== finalUrl.trim());
  const hintPhones = (hints.data?.phones || []).filter((p) => p.number.replace(/\D/g, "") !== phone.replace(/\D/g, ""));
  // A number already on file (the default, a CTM number, an ad's call button) goes out exactly as stored
  // ("+19162997920"); only how it reads here changed. A number typed fresh goes as typed.
  const storedPhone = (value: string) => [defaults.phone, ...defaults.ctm_numbers, ...(hints.data?.phones || []).map((p) => p.number)]
    .find((n) => n && samePhone(n, value))?.trim() ?? value.trim();
  const [hours, setHours] = useState<CallHours>(defaults.call_hours);
  const [allHours, setAllHours] = useState(false);
  const [busy, setBusy] = useState("");
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [failure, setFailure] = useState("");
  const [notes, setNotes] = useState<Record<number, GroupNote>>({});
  const [assetsNote, setAssetsNote] = useState<{ danger: boolean; text: string } | null>(null);

  const allKeywords = groups.flatMap((g) => lines(g.keywords));
  const brand = isBrandCampaign(name, allKeywords, defaults.brand_terms);
  const bid = brand ? bidding : "max_conversions";
  const spec = () => ({
    name, daily_budget: Number(budget), bidding: bid, ...(bid === "manual_cpc" ? { max_cpc: Number(maxCpc) } : {}),
    locations: places, match_type: match, final_url: finalUrl.trim(), negatives: lines(negatives),
    ad_groups: groups.map((g) => ({
      name: g.name.trim(), keywords: lines(g.keywords), final_url: g.final_url.trim(), path1: g.path1, path2: g.path2,
      ads: g.ads.map((a) => ({ headlines: lines(a.headlines), descriptions: lines(a.descriptions) })),
    })),
    sitelinks: sitelinks.filter(filled), callouts: lines(callouts), snippet: { header: "Services", values: lines(services) },
    phone: storedPhone(phone), call_hours: allHours ? "always" : hours,
  });
  // Any edit makes the last check stale.
  useEffect(() => { setCheck(null); setFailure(""); }, [account, name, budget, bidding, maxCpc, places, match, finalUrl, groups, negatives, sitelinks, callouts, services, phone, hours, allHours]);
  const errorsFor = (...fields: string[]) => (check?.errors || []).filter((e) => fields.includes(e.field));
  const warnings = check?.warnings || [];

  const updateGroup = (id: number, patch: Partial<Group>) => setGroups((list) => list.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const updateAd = (id: number, j: number, patch: Partial<AdText>) => {
    setGroups((list) => list.map((g) => (g.id === id ? { ...g, ads: g.ads.map((a, k) => (k === j ? { ...a, ...patch } : a)) } : g)));
    setNotes((n) => (n[id]?.problems ? { ...n, [id]: {} } : n));
  };

  async function runCheck() {
    setBusy("check"); setFailure("");
    try { setCheck(await api<CheckResult>(`/api/ads-manager/${clientId}/campaigns/check`, { method: "POST", body: JSON.stringify({ account, spec: spec() }) })); }
    catch (e) { setFailure(message(e, "Google could not check it just now.")); }
    finally { setBusy(""); }
  }
  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy("create"); setFailure("");
    try {
      const r = await api<ProposeResult>(`/api/ads-manager/${clientId}/campaigns`, { method: "POST", body: JSON.stringify({ account, spec: spec(), apply_now: admin }) });
      onDone(r.message);
      navigate(back);
    } catch (err) { setFailure(message(err, "The campaign was not created.")); }
    finally { setBusy(""); }
  }
  async function draftAds(g: Group) {
    setBusy(`draft${g.id}`);
    setNotes((n) => ({ ...n, [g.id]: {} }));
    try {
      const r = await api<{ ads: DraftAd[] }>(`/api/ads-manager/${clientId}/ad-copy`, {
        method: "POST",
        body: JSON.stringify({ ad_group: g.name.trim() || lines(g.keywords)[0] || defaults.client.industry || "", keywords: lines(g.keywords), city: places[0]?.name || "", final_url: g.final_url.trim() || finalUrl.trim() }),
      });
      setGroups((list) => list.map((x) => (x.id === g.id ? { ...x, ads: x.ads.map((a, j) => (r.ads[j] ? { headlines: r.ads[j].headlines.join("\n"), descriptions: r.ads[j].descriptions.join("\n") } : a)) } : x)));
      setNotes((n) => ({ ...n, [g.id]: { problems: r.ads.map((a) => a.problems || []) } }));
    } catch (err) { setNotes((n) => ({ ...n, [g.id]: { error: message(err, "The AI could not draft it.") } })); }
    finally { setBusy(""); }
  }
  async function draftExtras() {
    setBusy("assets"); setAssetsNote(null);
    try {
      const g0 = groups[0];
      const r = await api<DraftAssets>(`/api/ads-manager/${clientId}/ad-assets`, {
        method: "POST",
        body: JSON.stringify({ final_url: finalUrl.trim(), service: g0?.name.trim() || lines(g0?.keywords || "")[0] || "", city: places[0]?.name || "" }),
      });
      // What the person already wrote stays; the draft fills the rest.
      setSitelinks((list) => {
        const kept = list.filter(filled);
        const have = new Set(kept.flatMap((l) => [l.text.trim().toLowerCase(), l.final_url.trim().toLowerCase()]));
        const merged = [...kept, ...r.sitelinks.filter((l) => !have.has(l.text.toLowerCase()) && !have.has(l.final_url.toLowerCase()))];
        return [...merged, ...Array.from({ length: Math.max(0, L.sitelinks[0] - merged.length) }, blankLink)];
      });
      const add = (text: string, more: string[]) => { const have = lines(text); const seen = new Set(have.map((t) => t.toLowerCase())); return [...have, ...more.filter((t) => !seen.has(t.toLowerCase()))].join("\n"); };
      setCallouts((t) => add(t, r.callouts));
      setServices((t) => add(t, r.snippet.values));
      if (r.message) setAssetsNote({ danger: false, text: r.message });
    } catch (err) { setAssetsNote({ danger: true, text: message(err, "The AI could not draft the extras.") }); }
    finally { setBusy(""); }
  }

  const first = groups[0];
  const firstAd = first?.ads[0];
  const preview = adPreview(first?.final_url.trim() || finalUrl, first?.path1 || "", first?.path2 || "", lines(firstAd?.headlines || ""), lines(firstAd?.descriptions || ""));
  const usedKeywords = new Set(allKeywords.map((k) => k.toLowerCase()));
  const saved = defaults.saved_keywords.filter((k) => !usedKeywords.has(k.keyword.toLowerCase())).slice(0, 24);
  const accountName = defaults.accounts.find((a) => a.customer_id === account)?.name;
  const rules = defaults.copy_rules;
  const ruleLine = [
    rules.forbidden.length ? `never ${rules.forbidden.map((w) => `"${w}"`).join(", ")}` : "",
    rules.excluded.length ? `no ${rules.excluded.join(", ")}` : "",
    rules.required_cta.length ? `every ad says ${rules.required_cta.map((c) => `"${c}"`).join(" and ")}` : "",
    rules.spelling ? `always "${rules.spelling}"` : "",
  ].filter(Boolean).join("; ");
  const ctm = defaults.ctm_numbers;
  const isCtm = ctm.some((n) => samePhone(n, phone));
  const unshown = (check?.errors || []).filter((e) => !SHOWN.test(e.field));
  const filledLinks = sitelinks.filter(filled);
  const calloutList = lines(callouts);
  const serviceList = lines(services);

  return (
    <form className="campaign-builder" onSubmit={create}>
      <Link className="ads-back" to={back}><ArrowLeft aria-hidden="true" />Campaigns</Link>
      <PageHeader title="New campaign" description={`A Google Search campaign for ${defaults.client.name}, built to the agency's Google Ads SOP. It is created paused; nothing spends until you turn it on.`} />

      <section className="builder-section">
        <h2>Budget and bidding</h2>
        <div className="builder-grid">
          {defaults.accounts.length > 1 && (
            <label className="builder-wide">Google Ads account
              <select value={account} onChange={(e) => setAccount(e.target.value)}>
                {defaults.accounts.map((a) => <option key={a.customer_id} value={a.customer_id}>{a.name} ({formatCustomerId(a.customer_id)})</option>)}
              </select>
            </label>
          )}
          <label className="builder-wide">Campaign name
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={L.name} />
          </label>
          <label>Daily budget
            <span className="campaigns-money"><span aria-hidden="true">$</span><input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="50" /></span>
            <small>{Number(budget) > 0 ? `About ${formatMoney(Number(budget) * 30.4, true)} a month. ` : ""}{formatMoney(L.min_daily_budget)} to {formatMoney(defaults.daily_cap)} a day.</small>
          </label>
        </div>
        <FieldErrors list={errorsFor("name", "daily_budget")} />
        {brand ? (
          <>
            <div className="builder-bids two" role="radiogroup" aria-label="Bidding">
              {([["max_conversions", "Max conversions", "Google sets bids to get the most leads for the budget."], ["manual_cpc", "Manual CPC", "You set the most to pay per click. Brand clicks are cheap."]] as const).map(([id, label, note]) => (
                <button key={id} type="button" role="radio" aria-checked={bidding === id} className={`builder-bid${bidding === id ? " active" : ""}`} onClick={() => setBidding(id)}>
                  <strong>{label}</strong><span>{note}</span>
                </button>
              ))}
            </div>
            <p className="builder-note builder-note-after">A brand campaign: its keywords search for the business by name, so Manual CPC is the SOP's choice.</p>
            {bidding === "manual_cpc" && (
              <label className="builder-narrow">Most to pay per click
                <span className="campaigns-money"><span aria-hidden="true">$</span><input inputMode="decimal" value={maxCpc} onChange={(e) => setMaxCpc(e.target.value)} placeholder="2.50" /></span>
              </label>
            )}
          </>
        ) : (
          <p className="builder-fixed"><strong>Max conversions</strong><span>Every new campaign starts here. A target cost per lead comes at 50 conversions a month.</span></p>
        )}
        <FieldErrors list={errorsFor("bidding", "max_cpc")} />
      </section>

      <section className="builder-section">
        <h2>Where ads show</h2>
        <p className="builder-note">Only people in these places see the ads.</p>
        <PlacePicker clientId={clientId} account={account} defaults={defaults} places={places} setPlaces={setPlaces} />
        <FieldErrors list={errorsFor("locations")} />
      </section>

      <section className="builder-section">
        <h2>Ad groups</h2>
        <p className="builder-note">One service per ad group, each with two ads: one emotional, one direct.{ruleLine ? ` This client: ${ruleLine}.` : ""}</p>
        <div className="builder-grid">
          <label className="builder-wide">Landing page
            <input type="url" inputMode="url" value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)} placeholder="https://" />
          </label>
          <label>Match
            <select value={match} onChange={(e) => setMatch(e.target.value as "phrase" | "exact")}>
              <option value="phrase">Phrase (recommended)</option>
              <option value="exact">Exact</option>
            </select>
          </label>
        </div>
        {hintUrls.length > 0 && (
          <div className="builder-saved">
            <span className="muted">In this account's ads:</span>
            {hintUrls.map((h) => <button key={h.url} type="button" className="pl-chip" title={h.url} onClick={() => setFinalUrl(h.url)}>{shortUrl(h.url)}</button>)}
          </div>
        )}
        <FieldErrors list={errorsFor("final_url", "match_type", "ad_groups")} />
        <div className="adgroups">
          {groups.map((g, i) => (
            <AdGroupCard
              key={g.id} group={g} index={i} limits={L} campaignUrl={finalUrl} saved={saved}
              canRemove={groups.length > 1} drafting={busy === `draft${g.id}`} busy={!!busy} note={notes[g.id]}
              errors={check?.errors || []}
              onChange={(patch) => updateGroup(g.id, patch)} onAd={(j, patch) => updateAd(g.id, j, patch)}
              onDraft={() => void draftAds(g)} onRemove={() => setGroups((list) => list.filter((x) => x.id !== g.id))}
            />
          ))}
        </div>
        {groups.length < L.ad_groups && (
          <button type="button" className="button secondary builder-add" onClick={() => setGroups((list) => [...list, blankGroup()])}><Plus aria-hidden="true" />Add ad group</button>
        )}
      </section>

      <section className="builder-section">
        <h2>Blocked searches</h2>
        <label className="builder-block">Negative keywords, one per line
          <textarea rows={5} value={negatives} onChange={(e) => setNegatives(e.target.value)} />
          <small>The playbook's research and job searches, plus this client's own. "free" is left out on purpose: "free estimate" is a buyer.</small>
        </label>
        <FieldErrors list={errorsFor("negatives")} />
      </section>

      <section className="builder-section">
        <div className="builder-section-head">
          <h2>Extras</h2>
          <button type="button" className="button secondary" disabled={!!busy || !finalUrl.trim()} onClick={() => void draftExtras()}><Sparkles aria-hidden="true" />{busy === "assets" ? "Reading the page…" : "Draft from the landing page"}</button>
        </div>
        {assetsNote && <p className={`inline-notice${assetsNote.danger ? " danger" : ""}`} role={assetsNote.danger ? "alert" : "status"}>{assetsNote.text}</p>}
        <h3 className="builder-subhead">Sitelinks</h3>
        <div className="sitelinks">
          {sitelinks.map((l, i) => {
            const set = (patch: Partial<Sitelink>) => setSitelinks((list) => list.map((x, k) => (k === i ? { ...x, ...patch } : x)));
            return (
              <div className="sitelink-row" key={i}>
                <label>Link text<input value={l.text} onChange={(e) => set({ text: e.target.value })} maxLength={L.sitelink_text} placeholder={["Book Online", "Pricing", "Reviews", "About Us"][i] || ""} /></label>
                <label>Page<input type="url" inputMode="url" value={l.final_url} onChange={(e) => set({ final_url: e.target.value })} placeholder="https://" /></label>
                <label>Description line 1<input value={l.description1} onChange={(e) => set({ description1: e.target.value })} maxLength={L.sitelink_description} /></label>
                <label>Description line 2<input value={l.description2} onChange={(e) => set({ description2: e.target.value })} maxLength={L.sitelink_description} /></label>
                <button type="button" className="sitelink-remove" aria-label={`Remove sitelink ${i + 1}`} onClick={() => setSitelinks((list) => (list.length > 1 ? list.filter((_, k) => k !== i) : [blankLink()]))}><X aria-hidden="true" /></button>
              </div>
            );
          })}
        </div>
        <div className="builder-row">
          {sitelinks.length < L.sitelinks[1] && <button type="button" className="text-button" onClick={() => setSitelinks((list) => [...list, blankLink()])}>Add sitelink</button>}
          <small className={filledLinks.length && filledLinks.length < L.sitelinks[0] ? "builder-count bad" : "builder-count"}>{filledLinks.length} of at least {L.sitelinks[0]}, each to its own page</small>
        </div>
        <FieldErrors list={errorsFor("sitelinks")} />
        <div className="builder-pair">
          <label>Callouts, one per line
            <textarea rows={5} value={callouts} onChange={(e) => setCallouts(e.target.value)} placeholder={"Licensed and Insured\nSame-Day Service\nUpfront Pricing\nLocally Owned"} />
            <Counter count={calloutList.length} min={L.callouts[0]} max={L.callouts[1]} noun="callouts" over={calloutList.filter((t) => t.length > L.callout)} limit={L.callout} />
          </label>
          <label>Services snippet, one per line
            <textarea rows={5} value={services} onChange={(e) => setServices(e.target.value)} placeholder={"Furniture Removal\nAppliance Hauling\nEstate Cleanouts"} />
            <Counter count={serviceList.length} min={L.snippet_values[0]} max={L.snippet_values[1]} noun="services" over={serviceList.filter((t) => t.length > L.snippet_value)} limit={L.snippet_value} />
          </label>
        </div>
        <FieldErrors list={errorsFor("callouts", "snippet")} />
      </section>

      <section className="builder-section">
        <h2>Call button</h2>
        <div className="builder-grid">
          <label>Phone number
            <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(916) 555-0142" />
            <small className={ctm.length && phone.trim() && !isCtm ? "builder-count bad" : undefined}>
              {!ctm.length ? "No CTM tracking number on file, so calls will not be tracked."
                : isCtm ? "The client's CTM tracking number."
                  : `Use a CTM tracking number: ${ctm.map((n) => phoneLabel(n) ?? n).join(", ")}.`}
            </small>
          </label>
        </div>
        {hintPhones.length > 0 && (
          <div className="builder-saved">
            <span className="muted">On this account's call buttons:</span>
            {hintPhones.map((p) => <button key={p.number} type="button" className="pl-chip" title={p.campaigns.join(", ")} onClick={() => setPhone(phoneLabel(p.number) ?? p.number)}>{phoneLabel(p.number) ?? p.number}</button>)}
          </div>
        )}
        <FieldErrors list={errorsFor("phone")} />
        <fieldset className="call-hours" disabled={allHours}>
          <legend>Shows while the phone is answered</legend>
          <div className="call-days" role="group" aria-label="Days">
            {DAYS.map((d) => {
              const on = hours.days.includes(d.id);
              return <button key={d.id} type="button" aria-pressed={on} className={`call-day${on ? " active" : ""}`} onClick={() => setHours((h) => ({ ...h, days: on ? h.days.filter((x) => x !== d.id) : DAYS.map((x) => x.id).filter((x) => x === d.id || h.days.includes(x)) }))}>{d.short}</button>;
            })}
          </div>
          <div className="call-times">
            <label>From
              <select value={hours.start_hour} onChange={(e) => setHours((h) => ({ ...h, start_hour: Number(e.target.value) }))}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </label>
            <label>To
              <select value={hours.end_hour} onChange={(e) => setHours((h) => ({ ...h, end_hour: Number(e.target.value) }))}>
                {Array.from({ length: 24 }, (_, k) => k + 1).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </label>
          </div>
        </fieldset>
        <label className="builder-check"><input type="checkbox" checked={allHours} onChange={(e) => setAllHours(e.target.checked)} />All hours</label>
        <FieldErrors list={errorsFor("call_hours")} />
      </section>

      <section className="builder-section">
        <h2>Preview</h2>
        <div className="builder-preview" aria-label="Ad preview">
          <div className="serp-ad">
            <span className="serp-sponsored">Sponsored</span>
            <span className="serp-url">{preview.url}</span>
            <strong className="serp-headline">{preview.headline}</strong>
            <p className="serp-description">{preview.description}</p>
            {calloutList.length > 0 && <p className="serp-callouts">{calloutList.slice(0, 4).map((c) => <span key={c}>{c}</span>)}</p>}
            {filledLinks.some((l) => l.text.trim()) && <p className="serp-sitelinks">{filledLinks.filter((l) => l.text.trim()).slice(0, 4).map((l, i) => <span key={i}>{l.text}</span>)}</p>}
            {phone.trim() && <span className="serp-call">Call {phoneLabel(phone) ?? phone.trim()}</span>}
          </div>
          <p className="builder-note builder-note-after">The first ad of the first ad group. Google mixes the headlines and descriptions; this shows the first ones.</p>
        </div>
      </section>

      {warnings.length > 0 && (
        <div className="builder-warnings" role="status">
          <h3>Worth a look before creating</h3>
          <ul>{warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
        </div>
      )}

      <div className="builder-footer">
        <div className="builder-footer-status" role="status">
          {failure ? <p className="inline-notice danger" role="alert">{failure}</p>
            : check?.ok ? <p className="builder-ok"><Check aria-hidden="true" />Google accepted every part of it.</p>
              : check && check.errors.length ? <FieldErrors list={unshown} fallback={`${check.errors.length} ${check.errors.length === 1 ? "thing needs" : "things need"} fixing above.`} />
                : <p className="muted">{accountName ? `Into ${accountName}. ` : ""}{admin ? "It is created paused." : "It goes to Approvals; a superadmin creates it."}</p>}
        </div>
        <div className="builder-footer-actions">
          <Link className="button secondary" to={back}>Cancel</Link>
          <button type="button" className="button secondary" disabled={!!busy} onClick={() => void runCheck()}>{busy === "check" ? "Checking…" : "Check with Google"}</button>
          <button type="submit" className="button" disabled={!!busy}>{busy === "create" ? "Working…" : admin ? "Create paused campaign" : "Send for approval"}</button>
        </div>
      </div>
    </form>
  );
}

function AdGroupCard({ group: g, index: i, limits: L, campaignUrl, saved, canRemove, drafting, busy, note, errors, onChange, onAd, onDraft, onRemove }: {
  group: Group; index: number; limits: Defaults["limits"]; campaignUrl: string; saved: Defaults["saved_keywords"];
  canRemove: boolean; drafting: boolean; busy: boolean; note?: GroupNote; errors: FieldNote[];
  onChange: (patch: Partial<Group>) => void; onAd: (j: number, patch: Partial<AdText>) => void; onDraft: () => void; onRemove: () => void;
}) {
  const [showSaved, setShowSaved] = useState(false);
  const f = `ad_groups.${i}`;
  const at = (...fields: string[]) => errors.filter((e) => fields.includes(e.field));
  const kw = lines(g.keywords);
  return (
    <div className="adgroup">
      <div className="adgroup-head">
        <label className="adgroup-name"><span className="sr-only">Ad group {i + 1} name</span>
          <input value={g.name} onChange={(e) => onChange({ name: e.target.value })} maxLength={L.name} placeholder={`Ad group ${i + 1}, e.g. Furniture removal`} aria-label={`Ad group ${i + 1} name`} />
        </label>
        <div className="adgroup-actions">
          <button type="button" className="button secondary" disabled={busy || (!kw.length && !g.name.trim())} onClick={onDraft}><Sparkles aria-hidden="true" />{drafting ? "Drafting…" : "Draft with AI"}</button>
          {canRemove && <button type="button" className="text-button" onClick={onRemove}>Remove</button>}
        </div>
      </div>
      {note?.error && <p className="inline-notice danger" role="alert">{note.error}</p>}
      <div className="adgroup-grid">
        <div className="adgroup-keywords">
          <label>Keywords, one per line
            <textarea rows={5} value={g.keywords} onChange={(e) => onChange({ keywords: e.target.value })} placeholder={"junk removal\njunk removal near me\njunk hauling"} />
          </label>
          {saved.length > 0 && <button type="button" className="text-button builder-saved-toggle" aria-expanded={showSaved} onClick={() => setShowSaved((v) => !v)}>{showSaved ? "Hide saved keywords" : "Add from the Keywords page"}</button>}
        </div>
        <div className="adgroup-where">
          <label><span>Landing page <em>If not the campaign's</em></span>
            <input type="url" inputMode="url" value={g.final_url} onChange={(e) => onChange({ final_url: e.target.value })} placeholder={campaignUrl || "https://"} />
          </label>
          <div className="builder-paths">
            <label>Display path<input value={g.path1} onChange={(e) => onChange({ path1: e.target.value })} maxLength={L.path} placeholder="junk-removal" /></label>
            <label><span className="sr-only">Display path, second part</span><input value={g.path2} onChange={(e) => onChange({ path2: e.target.value })} maxLength={L.path} placeholder="sacramento" aria-label="Display path, second part" /></label>
          </div>
        </div>
      </div>
      {showSaved && saved.length > 0 && (
        <div className="builder-saved">
          {saved.map((k) => <button key={k.keyword} type="button" className="pl-chip" onClick={() => onChange({ keywords: `${g.keywords.trim() ? `${g.keywords.trim()}\n` : ""}${k.keyword}` })}>+ {k.keyword}</button>)}
        </div>
      )}
      <FieldErrors list={at(`${f}.name`, `${f}.keywords`, `${f}.final_url`, `${f}.path`, `${f}.ads`)} />
      <div className="adgroup-ads">
        {g.ads.map((ad, j) => {
          const hl = lines(ad.headlines);
          const ds = lines(ad.descriptions);
          const problems = note?.problems?.[j] || [];
          return (
            <div className="adgroup-ad" key={j}>
              <h3>Ad {j + 1} <span>{AD_STYLES[j] || ""}</span></h3>
              <label>Headlines, one per line
                <textarea rows={8} value={ad.headlines} onChange={(e) => onAd(j, { headlines: e.target.value })} placeholder={j === 0 ? "Junk Removal Sacramento\nBreathe Easy Again\nBook Your Pickup Today" : "Junk Removal Sacramento\nLicensed And Insured\nGet A Free Estimate"} />
                <Counter count={hl.length} min={L.headlines[0]} max={L.headlines[1]} noun="headlines" over={hl.filter((t) => t.length > L.headlines[2])} limit={L.headlines[2]} />
              </label>
              <label>Descriptions, one per line
                <textarea rows={4} value={ad.descriptions} onChange={(e) => onAd(j, { descriptions: e.target.value })} />
                <Counter count={ds.length} min={L.descriptions[0]} max={L.descriptions[1]} noun="descriptions" over={ds.filter((t) => t.length > L.descriptions[2])} limit={L.descriptions[2]} />
              </label>
              {problems.length > 0 && <ul className="builder-draft-notes">{problems.map((p, k) => <li key={k}>{p}</li>)}</ul>}
              <FieldErrors list={at(`${f}.ads.${j}`)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldErrors({ list, fallback }: { list: FieldNote[]; fallback?: string }) {
  if (!list.length) return fallback ? <p className="builder-error" role="alert">{fallback}</p> : null;
  return <ul className="builder-errors" role="alert">{list.map((e, i) => <li key={i}>{e.message}</li>)}</ul>;
}

function Counter({ count, min, max, noun, over, limit }: { count: number; min: number; max: number; noun: string; over: string[]; limit: number }) {
  const bad = count < min || count > max || over.length > 0;
  return (
    <small className={bad && count > 0 ? "builder-count bad" : "builder-count"}>
      {count} of {min} to {max} {noun}{over.length ? `. Over ${limit} characters: ${over.map((t) => `"${t}" (${t.length})`).join(", ")}` : ""}
    </small>
  );
}

function PlacePicker({ clientId, account, defaults, places, setPlaces }: { clientId: number; account: string; defaults: Defaults; places: SpecLocation[]; setPlaces: (fn: (p: SpecLocation[]) => SpecLocation[]) => void }) {
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Place[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [miles, setMiles] = useState("15");
  const added = useMemo(() => new Set(places.map((p) => (p.type === "place" ? p.id : `r:${p.name}`))), [places]);
  async function find() {
    if (!q.trim()) return;
    setBusy(true); setError("");
    try {
      const r = await api<{ places: Place[] }>(`/api/ads-manager/${clientId}/places?q=${encodeURIComponent(q.trim())}${account ? `&account=${account}` : ""}`);
      setFound(r.places);
    } catch (e) { setError(e instanceof Error ? e.message : "Google could not look that up."); }
    finally { setBusy(false); }
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") { e.preventDefault(); void find(); } };
  const radiusFor = (l: Defaults["locations"][number]) => ({ type: "radius" as const, miles: Number(miles) || 15, address: { city: l.city || "", state: l.state || "" }, name: [l.city, l.state].filter(Boolean).join(", ") || l.name });
  const withCity = defaults.locations.filter((l) => l.city && l.state);
  return (
    <div className="place-picker">
      <div className="place-search">
        <label className="sr-only" htmlFor="place-q">City, ZIP code or county</label>
        <input id="place-q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="City, ZIP code or county. Separate several with ;" />
        <button type="button" className="button secondary" disabled={busy || !q.trim()} onClick={() => void find()}><Search aria-hidden="true" />{busy ? "Finding…" : "Find"}</button>
      </div>
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
      {found && (
        found.length ? (
          <ul className="place-results">
            {found.map((p) => (
              <li key={p.id}>
                <span><strong>{p.name}</strong><span className="muted"> {p.canonical_name !== p.name ? p.canonical_name : ""}{p.type ? ` (${p.type.toLowerCase().replace(/_/g, " ")})` : ""}</span></span>
                {added.has(p.id) ? <span className="muted">Added</span>
                  : <button type="button" className="text-button" onClick={() => setPlaces((list) => [...list, { type: "place", id: p.id, name: p.canonical_name || p.name }])}>Add</button>}
              </li>
            ))}
          </ul>
        ) : <p className="muted">Google found no place by that name in the US.</p>
      )}
      {withCity.length > 0 && (
        <div className="place-radius">
          <span className="muted">Or a radius of</span>
          <span className="campaigns-miles"><input inputMode="numeric" value={miles} onChange={(e) => setMiles(e.target.value)} aria-label="Miles" /> miles around</span>
          {withCity.map((l) => {
            const r = radiusFor(l);
            return <button key={l.id} type="button" className="pl-chip" disabled={added.has(`r:${r.name}`)} onClick={() => setPlaces((list) => [...list, r])}><MapPin aria-hidden="true" />{r.name}</button>;
          })}
        </div>
      )}
      {places.length > 0 && (
        <ul className="place-chosen" aria-label="Places chosen">
          {places.map((p, i) => (
            <li key={`${p.type}${p.type === "place" ? p.id : p.name}${i}`}>
              <span>{p.type === "radius" ? `${p.miles} mi around ${p.name}` : p.name}</span>
              <button type="button" aria-label={`Remove ${p.name}`} onClick={() => setPlaces((list) => list.filter((_, j) => j !== i))}><X aria-hidden="true" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
